import { createSlice, Draft, PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import { Pax } from 'gql/autogen/Pax';
import { AvailabilityResponse } from 'gql/autogen/AvailabilityResponse';
import queryActivitiesGqlApi, { resetStateWithDefaultApiBaseState } from 'store/api/activitiesGqlApi';
import { DefaultApiBaseState } from 'store/api/ApiConstants';
import { ActivityGqlOperation, ApiBaseState, AvailabilityRequestProps, CalendarRequestProps, GqlApiResponse } from 'store/api/api.types';
import { updateApiBaseState } from 'store/api/apiUtils';
import { getDateInYmdFormat, getSelectedDate, setTime } from 'core-framework/util/datetime/date';
import {
    AvailabilitySearchCriteriaStatus,
    getAvailabilitySearchCriteriaStatus,
    getTotalPax,
    initialPaxState,
    toEnrichedBookingRequirement,
} from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxUtils';
import {
    getCheapestOfferOption,
    getEnrichedOfferIdFromQueryParams,
    getOfferFromSelectedOptionParameters,
    getOfferOptionIdFromQueryParams,
    getOfferWithMinPricingSummary,
    getTravelDateFromQueryParams,
} from 'gql/service/graph/activityService';
import { measureAvailabilityApiResponse } from 'store/api/measurements/availabilityGqlMeasurements';
import { Optional } from 'core/type/generic';
import {
    BookingRequirementState,
    ContinueYourBookingNotificationType,
    EnrichedBookingRequirement,
    OfferNotificationType,
    OfferOptionsState,
    SelectedOfferState,
} from 'component/page/detail/store/availability.types';
import { CalendarResponse } from 'gql/autogen/CalendarResponse';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { Specification } from 'gql/autogen/Specification';
import { AvailabilityCalendar } from 'gql/autogen/AvailabilityCalendar';
import { getBootstrapConfig } from 'core/window/windowService';
import { ExperimentId } from 'core/type/ExperimentId';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { getWindow } from 'core-framework/util/Dom';
import { Offer } from 'gql/autogen/Offer';
import { getFirstAvailableEnrichedOfferOption } from 'component/page/detail/PackageOptions/PackageOptionsUtils';

export interface AvailabilityState extends ApiBaseState {
    /** @deprecated use offerOptionsState instead */
    passengers: Pax[]; // TODO: Remove this when integrate ACT-4944
    selectedDate: Date;
    earliestAvailableDate?: Date;
    availabilityResponse?: AvailabilityResponse;
    selectedBookingRequirement: Required<EnrichedBookingRequirement>;
    isSearchCriteriaUpdated: boolean;
    isValidSearchCriteria: boolean;
    searchCriteriaStatus: AvailabilitySearchCriteriaStatus;
    totalPax: number;
    selectedOffer: SelectedOfferState;
    offerOptionsState: OfferOptionsState;
    offerNotificationState: Record<string, boolean>;
    detailsPageNotificationType: Record<ContinueYourBookingNotificationType, boolean>;
}

export const availabilityInitialState: AvailabilityState = {
    ...DefaultApiBaseState,
    passengers: [],
    selectedDate: getSelectedDate(),
    earliestAvailableDate: undefined,
    selectedBookingRequirement: {
        id: -1,
        adultRequired: true,
        minPax: 0,
        maxPax: 1,
        paxRequirements: [],
    },
    isSearchCriteriaUpdated: false,
    isValidSearchCriteria: true,
    searchCriteriaStatus: AvailabilitySearchCriteriaStatus.NoError,
    totalPax: 0,
    selectedOffer: {
        offer: undefined,
        offerIndex: undefined,
    },
    offerOptionsState: {},
    offerNotificationState: {
        [OfferNotificationType.DateChip]: false,
        [OfferNotificationType.OptionSpec]: false,
    },
    detailsPageNotificationType: {
        [ContinueYourBookingNotificationType.OfferNotAvailable]: false,
        [ContinueYourBookingNotificationType.DateNotAvailable]: false,
    },
};

const setSelectedBookingRequirement = (state: Draft<AvailabilityState>, bookingRequirement: EnrichedBookingRequirement, shouldResetToInitPaxState = false) => {
    state.selectedBookingRequirement.adultRequired = !!bookingRequirement.adultRequired;
    state.selectedBookingRequirement.id = bookingRequirement.id ?? -1;
    state.selectedBookingRequirement.maxPax = bookingRequirement.maxPax ?? 1;
    state.selectedBookingRequirement.minPax = bookingRequirement.minPax ?? 0;
    state.selectedBookingRequirement.paxRequirements = bookingRequirement.paxRequirements;
};

const setSelectedBookingRequirementWithDefaults = (state: Draft<AvailabilityState>, bookingRequirement: EnrichedBookingRequirement, shouldResetToInitPaxState = false) => {
    const config = getBootstrapConfig();
    const isAct4603 = isB(ExperimentId.ACT_4603, config.experiments);
    const offerId = state.selectedOffer.offer?.offerGroupId || -1;
    const offerState = state.offerOptionsState[offerId];
    if (offerState) {
        offerState.passengers = initialPaxState(bookingRequirement, isAct4603, shouldResetToInitPaxState ? undefined : offerState.passengers);
    }
    setSelectedBookingRequirement(state, bookingRequirement, shouldResetToInitPaxState);
};

const resetAvailabilityStateInternal = (state: AvailabilityState, shouldResetSelectedDate = true) => {
    resetStateWithDefaultApiBaseState(state);
    const enrichedBookingRequirement = state.selectedBookingRequirement;
    setSelectedBookingRequirementWithDefaults(state, enrichedBookingRequirement, true);
    state.isSearchCriteriaUpdated = availabilityInitialState.isSearchCriteriaUpdated;
    state.isValidSearchCriteria = availabilityInitialState.isValidSearchCriteria;
    state.searchCriteriaStatus = availabilityInitialState.searchCriteriaStatus;
    state.totalPax = availabilityInitialState.totalPax;
    state.offerOptionsState = availabilityInitialState.offerOptionsState;

    if (shouldResetSelectedDate) {
        state.selectedDate = state.earliestAvailableDate || state.selectedDate;
        state.availabilityResponse = undefined;
        state.earliestAvailableDate = undefined;
    }
};

export const filterPossibleOfferOptions = (offerOptions: EnrichedOfferOption[], selectedOptions: Specification[]) => {
    if (!selectedOptions.length) return offerOptions;

    return offerOptions.filter(({ offerOptionId, isAvailable, specification = [] }) => {
        if (!isAvailable || offerOptionId == null || offerOptionId < 0) return false;

        return selectedOptions.every(({ key, specification: selectedSpec }) =>
            specification.some(({ key: specKey, specification: specValue }) => specKey === key && specValue === selectedSpec),
        );
    });
};

const setUnAvailableDate = (state: AvailabilityState, selectedOfferId: number) => {
    const selectedOffer = state.offerOptionsState[selectedOfferId];
    selectedOffer.selectedSpecifications = [];
    selectedOffer.allOfferOptions = [];
    selectedOffer.possibleOfferOptions = [];
    if (selectedOffer.selectedDate) {
        selectedOffer.unavailableDatesFromAvailabilityResponse = [...(selectedOffer.unavailableDatesFromAvailabilityResponse || []), selectedOffer.selectedDate];
        selectedOffer.selectedDate = undefined;
    }
};

const getEarliestAvailableDate = (adjustedTimezoneDate: Date, availabilityCalendar?: AvailabilityCalendar) => {
    const selectedDate = getSelectedDate();
    const availableDateArray =
        availabilityCalendar?.schedules
            ?.slice(1)
            .filter((schedule) => schedule.isAvailable)
            .map((schedule) => new Date(schedule.date || selectedDate)) || [];
    if (!availableDateArray || availableDateArray.length === 0) {
        return adjustedTimezoneDate;
    }
    const indexOfSelectedDate = availableDateArray.map(Number).indexOf(+selectedDate);
    if (indexOfSelectedDate >= 0) {
        return setTime(availableDateArray[indexOfSelectedDate], adjustedTimezoneDate);
    }
    return setTime(availableDateArray[0], adjustedTimezoneDate);
};

// Please rename to getEarliestAvailableDate and delete the old one once integrate ACT-4940 Continue your booking
const getEarliestAvailableDateWithSelectedDate = (selectedDate: Date, availabilityCalendar?: AvailabilityCalendar) => {
    const defaultDate = getSelectedDate();
    const availableDateArray = availabilityCalendar?.schedules?.filter((schedule) => schedule.isAvailable).map((schedule) => new Date(schedule.date || defaultDate)) || [];
    if (!availableDateArray || availableDateArray.length === 0) {
        return selectedDate;
    }
    const indexOfSelectedDate = availableDateArray.map((date) => getDateInYmdFormat(date)).indexOf(getDateInYmdFormat(selectedDate));
    if (indexOfSelectedDate >= 0) {
        return setTime(availableDateArray[indexOfSelectedDate], selectedDate);
    }
    return setTime(availableDateArray[0], selectedDate);
};

const setEarliestAvailableDate = (state: AvailabilityState, action: PayloadAction<{ result: CalendarResponse; props: CalendarRequestProps }>) => {
    const { result, props } = action.payload;
    if (props.isAdditionalCalendar) return;
    const availabilityCalendar = result.result?.calendar;
    const isContinueYourBookingFeature = isOn(FeatureName.ContinueYourBooking);
    const locationSearch = getWindow()?.location.search;
    const travelDate = getTravelDateFromQueryParams(locationSearch);
    if (availabilityCalendar) {
        const earliestAvailableDate = isContinueYourBookingFeature
            ? getEarliestAvailableDateWithSelectedDate(state.selectedDate, availabilityCalendar)
            : getEarliestAvailableDate(state.selectedDate, availabilityCalendar);
        if (earliestAvailableDate > state.selectedDate) {
            state.earliestAvailableDate = earliestAvailableDate;
            state.selectedDate = earliestAvailableDate;
            if (isContinueYourBookingFeature && travelDate) {
                state.detailsPageNotificationType[ContinueYourBookingNotificationType.DateNotAvailable] = true;
            }
        } else if (isContinueYourBookingFeature) {
            const selectedDateSchedule = availabilityCalendar.schedules?.find((schedule) => schedule.date === getDateInYmdFormat(state.selectedDate));
            if (selectedDateSchedule && selectedDateSchedule.isAvailable) {
                state.earliestAvailableDate = state.selectedDate;
            } else {
                state.earliestAvailableDate = earliestAvailableDate;
                state.selectedDate = earliestAvailableDate;
                if (travelDate) state.detailsPageNotificationType[ContinueYourBookingNotificationType.DateNotAvailable] = true;
            }
        } else {
            state.earliestAvailableDate = state.selectedDate;
        }
    }
};

const getSelectedBookingRequirement = (availabilityResponse: AvailabilityResponse | undefined, bookingRequirementId: number): Optional<EnrichedBookingRequirement> => {
    const bookingRequirements = availabilityResponse?.result?.activity?.offerPricing?.bookingRequirements || [];
    const paxRequirements = availabilityResponse?.result?.activity?.offerPricing?.paxRequirements || [];
    if (bookingRequirements.length > 0) {
        // we need to map bookingRequirement and bookingRequirementRefId correctly from BE side for Viator case
        if (bookingRequirementId < 0) {
            return toEnrichedBookingRequirement(bookingRequirements[0], paxRequirements);
        }
        const bookingRequirement = bookingRequirements.find((br) => br.id === bookingRequirementId);
        if (bookingRequirement) {
            return toEnrichedBookingRequirement(bookingRequirement, paxRequirements);
        }
    }
    return undefined;
};

const internalSetOfferOption = (state: Draft<AvailabilityState>, offerGroupId: number, selectedSpecifications: Specification[]) => {
    const targetOfferOption = state.offerOptionsState[offerGroupId];
    if (targetOfferOption) {
        targetOfferOption.selectedSpecifications = selectedSpecifications;
        const possibleOfferOptions = filterPossibleOfferOptions(targetOfferOption.allOfferOptions || [], selectedSpecifications);
        if (possibleOfferOptions.length === 0) {
            // reset to allOfferOptions when no possibleOfferOptions
            targetOfferOption.possibleOfferOptions = targetOfferOption.allOfferOptions;
            targetOfferOption.selectedSpecifications = [];
            state.searchCriteriaStatus = AvailabilitySearchCriteriaStatus.NoError;
        } else {
            targetOfferOption.possibleOfferOptions = possibleOfferOptions;
            const config = getBootstrapConfig();
            const availableQuantity = isB(ExperimentId.ACT_4598, config.experiments) ? (possibleOfferOptions[0].availableQuantity ?? Infinity) : Infinity;
            state.searchCriteriaStatus = getAvailabilitySearchCriteriaStatus(state.selectedBookingRequirement, targetOfferOption.passengers, state.totalPax, availableQuantity);
        }
    }
};

export const setBookingRequirements = (offer: Offer, state: Draft<AvailabilityState>) => {
    const enrichedOfferOption = offer?.enrichedOfferOption || [];
    const firstAvailableOfferOption = getFirstAvailableEnrichedOfferOption(enrichedOfferOption);
    const firstEnrichedOfferOption = enrichedOfferOption[0];
    const bookingRequirementId = firstAvailableOfferOption?.bookingRequirementRefId ?? firstEnrichedOfferOption?.bookingRequirementRefId ?? -1;
    const selectedBookingRequirement = getSelectedBookingRequirement(state.availabilityResponse, bookingRequirementId);
    if (!selectedBookingRequirement) return;
    setSelectedBookingRequirement(state, selectedBookingRequirement);
};

export const setBookingRequirementsInOfferLevel = (offer: Offer, state: Draft<AvailabilityState>, availabilityResponse: AvailabilityResponse | undefined) => {
    const enrichedOfferOption = offer?.enrichedOfferOption || [];
    const firstAvailableOfferOption = getFirstAvailableEnrichedOfferOption(enrichedOfferOption);
    const firstEnrichedOfferOption = enrichedOfferOption[0];
    const bookingRequirementId = firstAvailableOfferOption?.bookingRequirementRefId ?? firstEnrichedOfferOption?.bookingRequirementRefId ?? -1;
    const selectedBookingRequirement = getSelectedBookingRequirement(availabilityResponse, bookingRequirementId);
    if (!selectedBookingRequirement) return;
    setSelectedBookingRequirement(state, selectedBookingRequirement);
};

let availabilityInitialStateLazy: AvailabilityState;

const availabilitySlice = createSlice({
    name: 'availability',
    initialState: (): AvailabilityState => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!availabilityInitialStateLazy) {
            availabilityInitialStateLazy = { ...availabilityInitialState, ...DefaultApiBaseState };
        }
        return availabilityInitialStateLazy;
    },
    reducers: {
        setPassengers: (state, action: PayloadAction<Pax[]>) => {
            const config = getBootstrapConfig();
            const offerId = state.selectedOffer.offer?.offerGroupId || -1;
            const selectedOfferOptionAvailableQuantity = state.offerOptionsState[offerId]?.possibleOfferOptions?.[0]?.availableQuantity || Infinity;
            const totalPax = getTotalPax(action.payload);
            const bookingRequirement = state.selectedBookingRequirement;
            const availableQuantity = isB(ExperimentId.ACT_4598, config.experiments) ? selectedOfferOptionAvailableQuantity : Infinity;
            const searchCriteriaStatus = getAvailabilitySearchCriteriaStatus(bookingRequirement, action.payload, totalPax, availableQuantity);
            if (state.offerOptionsState[offerId]) state.offerOptionsState[offerId].passengers = action.payload;
            state.totalPax = totalPax;
            state.searchCriteriaStatus = searchCriteriaStatus;
            state.isSearchCriteriaUpdated = true;
            state.isValidSearchCriteria = searchCriteriaStatus === AvailabilitySearchCriteriaStatus.NoError;
        },
        setSelectedDate: (state, action: PayloadAction<{ selectedDate: Date }>) => {
            state.selectedDate = action.payload.selectedDate;
            state.isSearchCriteriaUpdated = true;
            // Activity level date overrides offer level date
            for (const offerOptionState of Object.values(state.offerOptionsState)) {
                offerOptionState.selectedDate = action.payload.selectedDate;
            }
        },
        setSelectedDateStateFromQueryParams: (state, action: PayloadAction<{ locationSearch: string }>) => {
            const selectedDate = getTravelDateFromQueryParams(action.payload.locationSearch);

            if (selectedDate) {
                state.selectedDate = selectedDate;
            } else {
                // Need to set again to handle case when browser back navigation
                state.selectedDate = state.earliestAvailableDate || availabilityInitialState.selectedDate;
            }

            for (const offerOptionState of Object.values(state.offerOptionsState)) {
                offerOptionState.selectedDate = state.selectedDate;
            }
        },
        setOffer: (state, action: PayloadAction<SelectedOfferState>) => {
            state.selectedOffer = action.payload;
        },
        setOfferOptionIndex: (state, action: PayloadAction<number>) => {
            const offerId = state.selectedOffer.offer?.offerGroupId || -1;
            if (state.offerOptionsState[offerId]) state.offerOptionsState[offerId].offerOptionId = action.payload;
        },
        setBookingRequirement: (state, action: PayloadAction<BookingRequirementState>) => {
            const bookingRequirementId = action.payload.bookingRequirementId;
            const selectedBookingRequirement = getSelectedBookingRequirement(state.availabilityResponse, bookingRequirementId);
            if (!selectedBookingRequirement) return;
            setSelectedBookingRequirementWithDefaults(state, selectedBookingRequirement);
            if (action.payload.isResetSearchCriteriaStatus) {
                state.searchCriteriaStatus = AvailabilitySearchCriteriaStatus.NoError;
                state.isSearchCriteriaUpdated = false;
                state.isValidSearchCriteria = true;
            }
        },
        resetAvailabilityState: (state) => {
            resetAvailabilityStateInternal(state);
        },
        availabilityLoadFinished: (state, action: PayloadAction<GqlApiResponse<AvailabilityResponse, AvailabilityRequestProps>>) => {
            // Must not update the ApiBaseState in Availability state when loading the BookingRequirements otherwise availability API call will fail
            updateApiBaseState(state, action);
            state.isSearchCriteriaUpdated = false;

            if (action.payload.baseState.isLoading) {
                return;
            }

            // Initialize offerOptionsState with each offer present in the response
            const payloadOffer = action.payload.result.result?.activity?.offerPricing?.offers || [];
            payloadOffer.forEach((offer) => {
                const offerId = offer.offerGroupId;
                if (offerId) {
                    state.offerOptionsState[offerId] = state.offerOptionsState[offerId] || {
                        passengers: [],
                        isCartEligible: offer.isCartEligible,
                        firstLandingOfferSelected: true,
                    };
                }
            });

            if (state.isError || state.isSuccess) {
                const availabilityResponse: AvailabilityResponse = action.payload.result;

                const activityForMeasurement = state.isSuccess ? availabilityResponse?.result?.activity || undefined : undefined;
                measureAvailabilityApiResponse(
                    state.isSuccess,
                    activityForMeasurement?.masterActivityId || 0,
                    action.payload.pageTypeId,
                    action.payload.config,
                    state.error,
                    activityForMeasurement,
                );

                // activity level
                if (!state.selectedOffer.offer) {
                    state.availabilityResponse = availabilityResponse;
                } else {
                    // Select previously selected when user changed date to save clicks for user
                    const selectedOfferId = state.selectedOffer.offer?.offerGroupId;
                    const responseOffers = availabilityResponse.result?.activity?.offerPricing?.offers || [];
                    const selectedResponseOffer = responseOffers.find((offer) => offer.offerGroupId === selectedOfferId);
                    const selectedStateOffer = selectedOfferId ? state.offerOptionsState[selectedOfferId] : undefined;

                    const navigation = getWindow()?.location.search;
                    const enrichedOfferIdFromQueryParams = getEnrichedOfferIdFromQueryParams(navigation) || undefined;
                    const offerOptionId = getOfferOptionIdFromQueryParams(navigation);

                    if (selectedResponseOffer && selectedStateOffer && selectedOfferId) {
                        const config = getBootstrapConfig();
                        if (isB(ExperimentId.AFT_1666, config.experiments)) {
                            setBookingRequirementsInOfferLevel(selectedResponseOffer, state, availabilityResponse);
                        } else {
                            isB(ExperimentId.ACT_5210, config.experiments) && setBookingRequirements(selectedResponseOffer, state);
                        }

                        if (!selectedStateOffer.selectedDate) {
                            selectedStateOffer.selectedDate = state.selectedDate;
                        }
                        if (selectedResponseOffer.enrichedOfferOption) {
                            const possibleOfferOptions = filterPossibleOfferOptions(
                                selectedResponseOffer.enrichedOfferOption || [],
                                selectedStateOffer.selectedSpecifications || [],
                            );

                            selectedStateOffer.possibleOfferOptions = possibleOfferOptions;
                            selectedStateOffer.allOfferOptions = selectedResponseOffer.enrichedOfferOption;
                            selectedStateOffer.basedCalendarOfferIds = selectedResponseOffer.enrichedOfferOption.map((offerOption) => offerOption.offerId);

                            const offerOption = getOfferFromSelectedOptionParameters(possibleOfferOptions, enrichedOfferIdFromQueryParams, offerOptionId);

                            const isEnableContinueYourBooking = isOn(FeatureName.ContinueYourBooking);
                            const isEnableMaintainSelectedOfferOnDetails = isOn(FeatureName.MaintainSelectedOfferOnDetails);
                            const isSortOfferOptionAscending = isOn(FeatureName.SortOfferOptionAscending);

                            const isOfferFromQueryParamsValid =
                                (isEnableContinueYourBooking || isEnableMaintainSelectedOfferOnDetails) && offerOption && enrichedOfferIdFromQueryParams === offerOption?.offerId;

                            if (isOfferFromQueryParamsValid) {
                                selectedStateOffer.firstLandingOfferSelected = false;
                                internalSetOfferOption(state, selectedOfferId, offerOption.specification || []);
                            } else if (selectedStateOffer.firstLandingOfferSelected) {
                                const cheapestOfferOption = isSortOfferOptionAscending
                                    ? getCheapestOfferOption(possibleOfferOptions)
                                    : getOfferWithMinPricingSummary(possibleOfferOptions);
                                if (cheapestOfferOption) {
                                    selectedStateOffer.firstLandingOfferSelected = false;
                                    internalSetOfferOption(state, selectedOfferId, cheapestOfferOption.specification || []);
                                }
                            }
                        }
                        if (!selectedResponseOffer.isAvailable && selectedOfferId) {
                            setUnAvailableDate(state, selectedOfferId);
                        }
                    } else if (selectedOfferId) {
                        setUnAvailableDate(state, selectedOfferId);
                    }
                }
            }
        },
        resetSelectedOfferCard: (state) => {
            state.selectedOffer = availabilityInitialState.selectedOffer;
        },
        setOfferOptionSelectedDate: (state, action: PayloadAction<{ offerId: number; selectedDate: Date }>) => {
            const targetOfferOption = state.offerOptionsState[action.payload.offerId];
            if (targetOfferOption) {
                targetOfferOption.selectedDate = action.payload.selectedDate;
                // Reset offerOptionId when select new date
                targetOfferOption.offerOptionId = undefined;
            }
        },
        setOfferOption: (state, action: PayloadAction<{ offerId: number; selectedOptions: Specification[] }>) => {
            internalSetOfferOption(state, action.payload.offerId, action.payload.selectedOptions);
        },
        setOfferNotification: (state, action: PayloadAction<Record<string, boolean>>) => {
            state.offerNotificationState = {
                ...state.offerNotificationState,
                ...action.payload,
            };
        },
        setContinueYourBookingNotification: (state, action: PayloadAction<Record<ContinueYourBookingNotificationType, boolean>>) => {
            state.detailsPageNotificationType = {
                ...availabilityInitialState.detailsPageNotificationType, // always set other notification to false
                ...action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        // Set earliestAvailable date from calendar response
        builder.addMatcher(
            (action: UnknownAction) => action.type === 'calendar/calendarLoadFinished',
            (state, action: PayloadAction<{ result: CalendarResponse; props: CalendarRequestProps }>) => {
                setEarliestAvailableDate(state, action);
            },
        );
    },
});

export const fetchAvailability = (activityToken: string, options: { excludePassengers?: boolean; enableOfferFilter?: boolean } = {}) => {
    const { excludePassengers = false, enableOfferFilter = false } = options;
    queryActivitiesGqlApi({ operation: ActivityGqlOperation.availability, args: { props: { activityToken, excludePassengers, enableOfferFilter, enableOffersGrouping: true } } });
};

export const {
    availabilityLoadFinished,
    resetAvailabilityState,
    setPassengers,
    setSelectedDate,
    setSelectedDateStateFromQueryParams,
    setOffer,
    setOfferOptionIndex,
    setBookingRequirement,
    resetSelectedOfferCard,
    setOfferOptionSelectedDate,
    setOfferOption,
    setOfferNotification,
    setContinueYourBookingNotification,
} = availabilitySlice.actions;

export default availabilitySlice.reducer;
